// Pure validation rules. Inputs: in-memory graph + floor metadata.
// Output: { blockers, warnings, info } where each entry is
// { code, message, refs: [{ kind, id }] }.
//
// Severity:
//   - blockers: prevent publish
//   - warnings: should be reviewed but may publish
//   - info:     diagnostics
//
// Categories tracked here mirror the validation philosophy in the spec.

export function runValidation(graph, floor) {
  const blockers = []
  const warnings = []
  const info = []

  if (!graph) return { blockers, warnings, info }
  const { nodes = [], edges = [], anchors = [], destinations = [], beacons = [] } = graph

  if (floor && nodes.length === 0) {
    warnings.push({ code: 'FLOOR_EMPTY', message: 'This floor has no waypoint graph yet.', refs: [] })
  }

  if (anchors.length === 0 && nodes.length > 0) {
    blockers.push({ code: 'NO_ENTRY_ANCHOR', message: 'Every floor must have at least one entry anchor.', refs: [] })
  }

  const adjacency = buildAdjacency(nodes, edges)
  const components = connectedComponents(nodes, adjacency)
  if (components.length > 1) {
    warnings.push({
      code: 'GRAPH_DISCONNECTED',
      message: `Graph has ${components.length} disconnected segments. Routes may not reach every destination.`,
      refs: [],
    })
  }

  for (const node of nodes) {
    const neighbors = adjacency.get(node.id)
    if (!neighbors || neighbors.length === 0) {
      warnings.push({
        code: 'NODE_ORPHAN',
        message: `Node "${node.name || node.id.slice(0, 8)}" has no edges.`,
        refs: [{ kind: 'node', id: node.id }],
      })
    }
  }

  for (const anchor of anchors) {
    if (!anchor.linked_node_id) {
      blockers.push({
        code: 'ANCHOR_UNLINKED',
        message: `Anchor "${anchor.name || anchor.external_code || anchor.id.slice(0, 8)}" is not linked to a node.`,
        refs: [{ kind: 'anchor', id: anchor.id }],
      })
    }
    if (!anchor.external_code && anchor.anchor_type === 'qr') {
      warnings.push({
        code: 'ANCHOR_NO_CODE',
        message: `QR anchor "${anchor.name || anchor.id.slice(0, 8)}" has no printable code.`,
        refs: [{ kind: 'anchor', id: anchor.id }],
      })
    }
  }

  const seenAnchorCodes = new Map()
  for (const anchor of anchors) {
    if (!anchor.external_code) continue
    const list = seenAnchorCodes.get(anchor.external_code) || []
    list.push(anchor.id)
    seenAnchorCodes.set(anchor.external_code, list)
  }
  for (const [code, ids] of seenAnchorCodes.entries()) {
    if (ids.length > 1) {
      blockers.push({
        code: 'ANCHOR_DUPLICATE_CODE',
        message: `QR code "${code}" is used by ${ids.length} anchors.`,
        refs: ids.map((id) => ({ kind: 'anchor', id })),
      })
    }
  }

  for (const dest of destinations) {
    if (!dest.linked_node_id && !dest.linked_anchor_id) {
      blockers.push({
        code: 'DESTINATION_UNLINKED',
        message: `Destination "${dest.name}" must be linked to at least one node.`,
        refs: [{ kind: 'destination', id: dest.id }],
      })
    }
  }

  if (anchors.length > 0 && nodes.length > 0) {
    const reachableFromAnchor = new Set()
    for (const anchor of anchors) {
      if (!anchor.linked_node_id) continue
      const queue = [anchor.linked_node_id]
      while (queue.length) {
        const next = queue.shift()
        if (reachableFromAnchor.has(next)) continue
        reachableFromAnchor.add(next)
        const neighbors = adjacency.get(next) || []
        for (const n of neighbors) queue.push(n)
      }
    }
    for (const dest of destinations) {
      if (!dest.linked_node_id) continue
      if (!reachableFromAnchor.has(dest.linked_node_id)) {
        blockers.push({
          code: 'DESTINATION_UNREACHABLE',
          message: `Destination "${dest.name}" is not reachable from any entry anchor.`,
          refs: [{ kind: 'destination', id: dest.id }],
        })
      }
    }
  }

  const seenBeaconKeys = new Map()
  for (const beacon of beacons) {
    const key = `${beacon.uuid || ''}:${beacon.major}:${beacon.minor}`
    const list = seenBeaconKeys.get(key) || []
    list.push(beacon.id)
    seenBeaconKeys.set(key, list)
  }
  for (const [key, ids] of seenBeaconKeys.entries()) {
    if (ids.length > 1 && key !== '::0:0') {
      warnings.push({
        code: 'BEACON_DUPLICATE',
        message: `Beacon identifier ${key} is used by ${ids.length} placements.`,
        refs: ids.map((id) => ({ kind: 'beacon', id })),
      })
    }
  }

  for (const anchor of anchors) {
    if (typeof anchor.z !== 'number' || Number.isNaN(anchor.z)) {
      warnings.push({
        code: 'ANCHOR_NO_HEIGHT',
        message: `Anchor "${anchor.name || anchor.id.slice(0, 8)}" has no Z height.`,
        refs: [{ kind: 'anchor', id: anchor.id }],
      })
    }
  }

  if (nodes.length >= 30) {
    info.push({
      code: 'GRAPH_DENSE',
      message: `${nodes.length} nodes on this floor — consider clustering with zones.`,
      refs: [],
    })
  }

  return { blockers, warnings, info }
}

export function buildAdjacency(nodes, edges) {
  const adj = new Map()
  for (const node of nodes) adj.set(node.id, [])
  for (const edge of edges) {
    if (!adj.has(edge.from_node_id) || !adj.has(edge.to_node_id)) continue
    adj.get(edge.from_node_id).push(edge.to_node_id)
    if (edge.directionality !== 'one_way') {
      adj.get(edge.to_node_id).push(edge.from_node_id)
    }
  }
  return adj
}

function connectedComponents(nodes, adjacency) {
  const visited = new Set()
  const components = []
  for (const node of nodes) {
    if (visited.has(node.id)) continue
    const queue = [node.id]
    const component = []
    while (queue.length) {
      const next = queue.shift()
      if (visited.has(next)) continue
      visited.add(next)
      component.push(next)
      const neighbors = adjacency.get(next) || []
      for (const n of neighbors) queue.push(n)
    }
    components.push(component)
  }
  return components
}
